import { CONFIG } from '../config';
import { WholeCandleSettings } from '@shared/types';
import { apiFetch } from './api';
import { DEFAULT_WHOLE_CANDLE_SETTINGS, normalizeToBaseSymbol } from '../store/settingsStore';

/**
 * Frontend Strategy Service
 * Communicates with backend strategy API
 * All strategy logic runs on the server - this is just an API client
 */
class StrategyService {
  // IMPORTANT: Use getter to call CONFIG.getBackendUrl() at runtime, not build-time frozen value
  private get baseUrl() { return CONFIG.getBackendUrl(); }
  private _enabled = true;
  private _isInitialized = false;
  // Per-symbol session high/low storage
  private _sessionHighLowBySymbol: Map<string, { high: number; low: number }> = new Map();
  private _currentSymbol: string = '';

  // ==================== Initialization ====================

  initializeStrategy(): void {
    this._isInitialized = true;
    console.log('✅ [STRATEGY SERVICE] Initialized (server-side strategy)');
  }

  isInitialized(): boolean {
    return this._isInitialized;
  }

  // ==================== Session High/Low (received from server via WebSocket) ====================

  getSessionHighLow(symbol?: string): { high: number; low: number } {
    const targetSymbol = symbol ? this._normalizeSymbol(symbol) : this._currentSymbol;
    const data = this._sessionHighLowBySymbol.get(targetSymbol);
    return data || { high: 0, low: 999999 };
  }

  updateSessionHighLow(symbol: string, high: number, low: number): void {
    const normalizedSymbol = this._normalizeSymbol(symbol);
    this._sessionHighLowBySymbol.set(normalizedSymbol, { high, low });
  }

  setCurrentSymbol(symbol: string): void {
    this._currentSymbol = this._normalizeSymbol(symbol);
  }

  private _normalizeSymbol(sym: string): string {
    if (!sym) return '';
    return normalizeToBaseSymbol(sym) || sym.toUpperCase();
  }

  // ==================== Enable/Disable ====================

  setEnabled(enabled: boolean): void {
    this._enabled = enabled;
    console.log(`🎯 [STRATEGY SERVICE] Strategy ${enabled ? 'enabled' : 'disabled'}`);
  }

  isEnabled(): boolean {
    return this._enabled;
  }

  // ==================== Backend API Methods ====================

  async getSettings(): Promise<WholeCandleSettings> {
    try {
      const response = await apiFetch(`${this.baseUrl}/api/strategy/settings`);
      if (!response.ok) throw new Error('Failed to fetch settings');
      return response.json();
    } catch (error) {
      console.error('Failed to fetch strategy settings:', error);
      return DEFAULT_WHOLE_CANDLE_SETTINGS;
    }
  }

  async updateSettings(settings: Partial<WholeCandleSettings>): Promise<void> {
    try {
      const response = await apiFetch(`${this.baseUrl}/api/strategy/settings`, {
        method: 'POST',
        body: JSON.stringify(settings),
      });
      if (!response.ok) throw new Error('Failed to update settings');
    } catch (error) {
      console.error('Failed to update strategy settings:', error);
    }
  }

  async recalculateMarkers(days?: number): Promise<{ success: boolean; count?: number; error?: string }> {
    try {
      const url = days
        ? `${this.baseUrl}/api/signal-markers/recalculate?days=${days}`
        : `${this.baseUrl}/api/signal-markers/recalculate`;
      const response = await apiFetch(url, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to recalculate markers');
      return await response.json();
    } catch (error) {
      console.error('Failed to recalculate markers:', error);
      return { success: false, error: String(error) };
    }
  }

  async recalculateVolumeMarkers(days?: number): Promise<{ success: boolean; count?: number; daysLimit?: number; error?: string }> {
    try {
      const url = days
        ? `${this.baseUrl}/api/volume-markers/recalculate?days=${days}`
        : `${this.baseUrl}/api/volume-markers/recalculate`;
      const response = await apiFetch(url, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to recalculate volume markers');
      return await response.json();
    } catch (error) {
      console.error('Failed to recalculate volume markers:', error);
      return { success: false, error: String(error) };
    }
  }
}

export const strategyService = new StrategyService();
